import { SectionedFormBuilder, useStepperContext } from '@folio/components';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import { useDistributionRequestControllerCreateManyDistributionRequests } from 'korint-sdk';
import { useAvailableProductIds } from 'korint-sdk/react';
import type { Field } from 'korint-sdk/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getResolver } from '../../components';

type DistributionRequestFunnelModuleProps = {
  fields?: Array<Field>;
  mode?: 'stepper' | 'standalone';
};

const DEFAULT_PRODUCT_FIELDS: Array<Field> = [
  { name: 'productIds', type: 'multiSelect', required: true, options: [] },
];

export function DistributionRequestFunnelModule({
  fields = DEFAULT_PRODUCT_FIELDS,
  mode = 'stepper',
}: DistributionRequestFunnelModuleProps) {
  const { t } = useTranslation();
  const { brokerageFirmId } = useParams();
  const availableProductIds = useAvailableProductIds();

  const resolvedFields = fields.map(field =>
    field.type === 'multiSelect' && field.name === 'productIds'
      ? { ...field, options: availableProductIds }
      : field,
  );

  const form = useForm({
    resolver: getResolver(resolvedFields),
  });
  const stepper = useStepperContext();

  const { mutateAsync: createDistributionRequest, isPending: isCreatingDistributionRequest } =
    useDistributionRequestControllerCreateManyDistributionRequests();

  if (!brokerageFirmId) {
    return <div>No brokerage firm id found in the url</div>;
  }

  const onSubmit = form.handleSubmit(async data => {
    const productId = data.productId;

    try {
      if (productId) {
        await createDistributionRequest({
          data: {
            brokerageFirmId,
            productIds: [productId as string],
          },
        });
      } else {
        await createDistributionRequest({
          data: {
            brokerageFirmId,
            productIds: data.productIds as string[],
          },
        });
      }
    } catch (error) {
      const status = (error as { errorResponse?: { status?: number } })?.errorResponse?.status;
      if (status !== 409) {
        throw error;
      }
    }
    if (mode === 'stepper' && stepper) {
      stepper.next();
    }
  });

  return (
    <SectionedFormBuilder
      sections={[
        { title: t('intermediaryFunnel.distributionRequest.title'), fields: resolvedFields },
      ]}
      form={form}
      isLoading={isCreatingDistributionRequest}
    >
      <FormBuilderFooter mode={mode} onSubmit={onSubmit} form={form} showPreviousButton />
    </SectionedFormBuilder>
  );
}
